import { RELIC_STANDIN_COLOUR, resolveColour, type ColourRoleName, type Hex, type ItemRarity } from "odyssey-shared";

/**
 * The item card's rarity colours (P2.8-T46, ADR-125 decisions 1.3 and 5; the design's `ItemCard` and `ItemArt`): the
 * rarity's colour through the edge, band, name, burst and sparks, and the solid mixes the design draws, since pixel art
 * keeps no alpha. Each colour is worked out here from the token table and written on the card as a custom property,
 * so the card's style sheet names no hex. Relic's colour is the stand-in `RELIC_STANDIN_COLOUR`, the card having none.
 */

/**
 * The token each rarity on the design's ladder takes: Epic the design's Very Rare (decision 1.3).
 */
const RARITY_TOKENS: Readonly<Record<Exclude<ItemRarity, "relic">, ColourRoleName>> = { common: "rarity-common", uncommon: "rarity-uncommon", rare: "rarity-rare", epic: "rarity-very-rare", legendary: "rarity-legendary" };

/**
 * The design's mixes: the band over `ink-700` at 0.22, the plate's inner ring over `ink-800` at 0.55, and the burst's
 * four rings from outermost to innermost over `ink-800`, halved for Common so "Common stays quiet".
 */
const MIX = { band: 0.22, plateRing: 0.55, burst: [0.12, 0.2, 0.3, 0.42] } as const;

/**
 * A card's colours: the rarity's own, the band's fill, the plate's inner ring, the burst's four rings from the
 * outermost, and the sparks'.
 */
export interface CardColours {
  readonly rarity: Hex;
  readonly band: Hex;
  readonly plateRing: Hex;
  readonly burst: readonly [Hex, Hex, Hex, Hex];
  readonly spark: Hex;
}

/**
 * A rarity's colour.
 *
 * @param rarity The rarity.
 * @returns The token's colour, or Relic's stand-in.
 * @example
 * rarityColour("epic");
 * // "#c080ff"
 */
export function rarityColour(rarity: ItemRarity): Hex {
  return rarity === "relic" ? (RELIC_STANDIN_COLOUR as Hex) : resolveColour(RARITY_TOKENS[rarity]);
}

/**
 * Mixes two colours into a solid one, as the design's `mixHex` does: each channel `a * t + b * (1 - t)`, rounded.
 *
 * @param a The colour weighted by `t`, `#rrggbb`.
 * @param b The colour weighted by `1 - t`, `#rrggbb`.
 * @param t The weight of `a`, 0 to 1.
 * @returns The mix, `#rrggbb` in lower case.
 * @throws RangeError when `t` is outside 0 to 1.
 * @example
 * mixColour("#ffa83a", "#12151c", 0.22);
 * // "#463523"
 */
export function mixColour(a: Hex, b: Hex, t: number): Hex {
  if (!(t >= 0 && t <= 1)) {
    throw new RangeError(`a mix's weight is 0 to 1, not ${t}`);
  }
  const channel = (hex: Hex, at: number): number => Number.parseInt(hex.slice(at, at + 2), 16);
  const parts = [1, 3, 5].map((at) => Math.round(channel(a, at) * t + channel(b, at) * (1 - t)).toString(16).padStart(2, "0"));
  return `#${parts.join("")}` as Hex;
}

/**
 * A rarity's colours on the card.
 *
 * @param rarity The rarity.
 * @returns The colours; the sparks are `vellum-100` as the design's are, and a Relic's are its own colour (proposed).
 * @example
 * cardColours("common").burst[0];
 * // the outermost ring: Common's colour over ink-800 at 0.06
 */
export function cardColours(rarity: ItemRarity): CardColours {
  const colour = rarityColour(rarity);
  const plate = resolveColour("ink-800");
  const quiet = rarity === "common" ? 0.5 : 1;
  const [outer, second, third, inner] = MIX.burst.map((t) => mixColour(colour, plate, t * quiet)) as [Hex, Hex, Hex, Hex];
  return {
    rarity: colour,
    band: mixColour(colour, resolveColour("ink-700"), MIX.band),
    plateRing: mixColour(colour, plate, MIX.plateRing),
    burst: [outer, second, third, inner],
    spark: rarity === "relic" ? colour : resolveColour("vellum-100"),
  };
}
